import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Sparkles } from "lucide-react";

const SERVICE_VISUAL = {
  "flower-bar": {
    bg: "var(--lf-mint)",
    accent: "var(--lf-mint-deep)",
    images: [
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_la-fleur-studio/artifacts/20plfgsf_3.webp",
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_la-fleur-studio/artifacts/28eg60gx_1.webp",
      "https://customer-assets.emergentagent.com/job_la-fleur-studio/artifacts/tdkc1b8n_2.jpeg",
    ],
    bullets: [],
  },
  bridal: {
    bg: "var(--lf-pink-pale)",
    accent: "var(--lf-pink-deep)",
    images: [
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_la-fleur-studio/artifacts/pyrilo0v_b2.webp",
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_la-fleur-studio/artifacts/wm3j46h8_b.webp",
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_la-fleur-studio/artifacts/ccccqzvu_IMG_0224.webp",
    ],
    bullets: [],
  },
  memorial: {
    bg: "var(--lf-lavender)",
    accent: "#a193b8",
    images: [
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_la-fleur-studio/artifacts/vqyeh1yh_funeral%203.webp",
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_la-fleur-studio/artifacts/lalqu55m_funeral%201.webp",
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_la-fleur-studio/artifacts/aelpcvho_funeral%20.webp",
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_la-fleur-studio/artifacts/lirgju36_4%20funeral%20.webp",
    ],
    bullets: [],
  },
  supplies: {
    bg: "var(--lf-cream-2)",
    accent: "var(--lf-gold-deep)",
    images: [
      "https://images.pexels.com/photos/6487798/pexels-photo-6487798.jpeg",
      "https://images.pexels.com/photos/17023112/pexels-photo-17023112.jpeg",
      "https://images.pexels.com/photos/18294720/pexels-photo-18294720.jpeg",
    ],
    bullets: [
      "Korean flower paper · premium ribbons · tissue paper",
      "Crowns · pearl bows · flower spray paints",
      "Real & artificial florals · flower food · cards",
      "Pre-order and pickup available · wholesale pricing",
    ],
  },
};

const ServicePage = () => {
  const { serviceId } = useParams();
  const [service, setService] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_date: "", message: "" });
  const [loading, setLoading] = useState(false);
  const v = SERVICE_VISUAL[serviceId] || SERVICE_VISUAL["flower-bar"];

  useEffect(() => {
    api.get(`/services/${serviceId}`).then(({ data }) => setService(data)).catch(() => setService(null));
    setForm({ name: "", email: "", phone: "", event_date: "", message: "" });
  }, [serviceId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please fill in your name, email, and message.");
    setLoading(true);
    try {
      await api.post("/inquiries", { ...form, service: serviceId });
      toast.success("Thank you — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", event_date: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please call us at (336) 269-0356.");
    } finally {
      setLoading(false);
    }
  };

  if (!service) {
    return (
      <div className="lf-container py-32 text-center" data-testid="service-not-found">
        <p className="font-serif text-3xl italic" style={{ color: "var(--lf-ink-soft)" }}>Service not found.</p>
        <Link to="/" className="lf-link mt-6 inline-block">← Back home</Link>
      </div>
    );
  }

  // Supplies page → show "Coming Soon" only
  if (serviceId === "supplies") {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" style={{ background: "var(--lf-cream-2)" }} data-testid="supplies-coming-soon">
        <div className="text-center max-w-lg px-6 py-16">
          <span className="lf-eyebrow" style={{ color: "var(--lf-gold-deep)" }}>Florist Supplies</span>
          <h1 className="font-cursive lf-gold-shimmer mt-4" style={{ fontSize: "clamp(3rem, 8vw, 6rem)" }}>Coming Soon</h1>
          <p className="font-serif italic mt-6 text-lg" style={{ color: "var(--lf-ink)" }}>
            We&rsquo;re preparing something beautiful for fellow florists &mdash; check back soon.
          </p>
          <div className="mt-8">
            <Link to="/" className="lf-btn lf-btn-outline">&larr; Back home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`service-page-${serviceId}`}>
      {/* HERO */}
      <section className="relative overflow-hidden" style={{ background: v.bg }}>
        <div className="lf-blob lf-blob-pink hidden md:block" style={{ width: 160, height: 160, top: -40, right: -20, opacity: 0.4 }} />
        <div className="lf-container py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-6">
            <span className="lf-eyebrow" style={{ color: v.accent }}>La Fleur Service</span>
            <h1 className="font-serif mt-4 leading-[0.94]" style={{ fontSize: "clamp(2.75rem, 6.5vw, 5.5rem)", fontWeight: 300, color: "var(--lf-ink)" }}>
              {service.name.split(/ & | · /)[0]}
              <br />
              <em style={{ color: "var(--lf-gold-deep)" }}>{service.name.split(/ & | · /).slice(1).join(" & ") || ""}</em>
            </h1>
            <p className="mt-6 font-script text-3xl" style={{ color: v.accent }}>{service.tagline}</p>
            <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed" style={{ color: "var(--lf-ink-soft)" }}>
              {service.description}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <a href="#inquiry" className="lf-btn lf-btn-gold" data-testid="service-inquiry-cta">
                {service.cta} <ArrowRight size={15} />
              </a>
              <a href="tel:+13362690356" className="lf-btn lf-btn-outline" data-testid="service-call-cta">
                <Phone size={14} /> (336) 269-0356
              </a>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}
            className="lg:col-span-6 grid grid-cols-12 gap-4 md:gap-6">
            <div className="col-span-7 lf-photo-card" style={{ aspectRatio: "4 / 5", background: "white", transform: "rotate(-2deg)" }}>
              <img src={v.images[0]} alt="" className="rounded-xl" />
            </div>
            <div className="col-span-5 flex flex-col gap-4 md:gap-6">
              <div className="lf-photo-card" style={{ aspectRatio: "1 / 1", background: "white", transform: "rotate(2deg)" }}>
                <img src={v.images[1]} alt="" className="rounded-xl" />
              </div>
              <div className="lf-photo-card" style={{ aspectRatio: "4 / 5", background: "white", transform: "rotate(-1deg)" }}>
                <img src={v.images[2]} alt="" className="rounded-xl" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* EXTRA GALLERY — shows images beyond the hero 3 */}
      {v.images.length > 3 && (
        <section className="lf-container pb-16 md:pb-24" data-testid="service-extra-gallery">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {v.images.slice(3).map((src, i) => (
              <div key={i} className="lf-photo-card" style={{ aspectRatio: "4 / 5", background: "white", transform: `rotate(${i % 2 === 0 ? -1 : 1}deg)` }}>
                <img src={src} alt="" className="rounded-xl" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* BULLETS */}
      {v.bullets && v.bullets.length > 0 && (
      <section className="lf-container py-20 md:py-24" data-testid="service-bullets">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-7">
          {v.bullets.map((b, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="p-7 md:p-8 flex gap-4 items-start"
              style={{ background: "white", borderRadius: 22, border: "1px solid var(--lf-border)" }}
            >
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center" style={{ width: 44, height: 44, borderRadius: 999, background: v.bg }}>
                  <Sparkles size={18} strokeWidth={1.5} style={{ color: v.accent }} />
                </div>
              </div>
              <p className="font-serif text-xl md:text-2xl leading-snug" style={{ color: "var(--lf-ink)", fontWeight: 500 }}>{b}</p>
            </motion.div>
          ))}
        </div>
      </section>
      )}

      {/* INQUIRY FORM */}
      <section id="inquiry" className="py-20 md:py-28" style={{ background: v.bg }} data-testid="service-inquiry-section">
        <div className="lf-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="lf-eyebrow" style={{ color: v.accent }}>Tell us about it</span>
            <h2 className="font-serif mt-3 leading-none" style={{ fontSize: "clamp(2rem, 4.5vw, 3rem)", fontWeight: 300, color: "var(--lf-ink)" }}>
              Send us
              <br />
              <em style={{ color: "var(--lf-gold-deep)" }}>your vision.</em>
            </h2>
            <p className="mt-5 text-base md:text-lg leading-relaxed max-w-md" style={{ color: "var(--lf-ink-soft)" }}>
              Share your date, palette, or details — we'll respond with a personalized quote, usually within a day.
            </p>
            <a href="tel:+13362690356" className="lf-btn lf-btn-outline mt-8 inline-flex" data-testid="service-call-secondary">
              <Phone size={14} /> Prefer to call? (336) 269-0356
            </a>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 p-8 md:p-10 space-y-4"
            style={{ background: "white", borderRadius: 28 }} data-testid="service-inquiry-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="lf-eyebrow" style={{ fontSize: "0.65rem" }}>Name</span>
                <input data-testid="service-form-name" className="lf-input mt-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" />
              </label>
              <label className="block">
                <span className="lf-eyebrow" style={{ fontSize: "0.65rem" }}>Email</span>
                <input data-testid="service-form-email" type="email" className="lf-input mt-2" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
              </label>
              <label className="block">
                <span className="lf-eyebrow" style={{ fontSize: "0.65rem" }}>Phone</span>
                <input data-testid="service-form-phone" className="lf-input mt-2" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(336) 000-0000" />
              </label>
              <label className="block">
                <span className="lf-eyebrow" style={{ fontSize: "0.65rem" }}>Event date</span>
                <input data-testid="service-form-date" type="date" className="lf-input mt-2" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
              </label>
            </div>
            <label className="block">
              <span className="lf-eyebrow" style={{ fontSize: "0.65rem" }}>Tell us about it</span>
              <textarea data-testid="service-form-message" rows={5} className="lf-input mt-2" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Date, palette, vision, headcount…" />
            </label>
            <button type="submit" disabled={loading} className="lf-btn lf-btn-gold w-full justify-center" data-testid="service-form-submit">
              {loading ? "Sending…" : "Send inquiry"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ServicePage;
